import { Algorithm } from "../../support/algorithm.support";
import { Optional } from "../../support/optional.model";
import { Tuple } from "../../support/tuple.model";
import { TypeSystem } from "../../support/type-system.support";

	/**
	 * @description
	 *	Niveles de la estructura comercial. Debe ser privado.
	 */

enum Level {
	Country = 0,
	Commerce = 1,
	Division = 2,
	Zone = 3,
	Area = 4
};

	/**
	 * @description
	 *  Modelo de una estructura comercial. Esta estructura comercial se
	 *	codifica en forma de árbol, donde cada nodo posee además una
	 *	referencia hacia su nodo padre, lo que permite recorrer eficientemente
	 *	la estructura comercial en ambos sentidos (i.e., desde y hacia la
	 *	raíz).
	 *
	 * @see gigot-backend/Core/Concept/CommercialStructure.cs
	 */

export class CommercialStructure {

	public static readonly ROOT_ID: number = 0;
	public static readonly ROOT_NAME: string = "ALL";
    public static readonly ROOT_ISINVOICED: number = 0;

	public id: number;
	public name: string;
	public parent: CommercialStructure;
	public subStructure: CommercialStructure[];
    public isInvoiced: number;

	/**
	 * @description
	 *	Construye un ID de área completo en formato ZZZAA, utilizando una zona
	 *	en formato ZZZ y un área en formato AA. Si los formatos son
	 *	incorrectos, devuelve 'undefined'.
	 */
	public static areaWithZoneOrUndefined(zoneId: number, areaWithoutZoneId: number): number {
		if (100 <= zoneId && zoneId <= 999) {
			if (0 <= areaWithoutZoneId && areaWithoutZoneId <= 99) {
				return 100 * zoneId + areaWithoutZoneId;
			}
		}
		return undefined;
	}

	/**
	 * @description
	 *	Construye un ID de área en formato AA (sin zona), desde un ID completo
	 *	en formato ZZZAA. Si el formato del área suministrada es incorrecto,
	 *	devuelve 'undefined'.
	 */
	public static areaWithoutZoneOrUndefined(areaWithZoneId: number): number {
		if (10000 <= areaWithZoneId && areaWithZoneId <= 99999) {
			return areaWithZoneId % 100;
		}
		else {
			return undefined;
		}
	}

	/**
	 * @description
	 *	Construye una estructura comercial con una semántica vacía. Nótese que
	 *	no es lo mismo que construir una estructura con valores por defecto.
	 *	Las reglas generales de construcción son:
	 *
	 *      - Campos 'array' se definen como [].
	 *      - Campos 'boolean' se definen como 'false'.
	 *      - Campos 'Date' se definen como 'undefined'.
	 *      - Campos 'enum' se definen como 'undefined'.
	 *      - Campos 'number' se definen como 'undefined'.
	 *      - Campos 'string' se definen como '' (cadena vacía).
	 *      - Campos 'object' se definen llamando a su propio método #empty().
	 *      - Campos de la superclase se definen al igual que campos 'object'.
	 *      - Campos genéricos se definen como 'undefined'.
	 *		- Campos recursivos se definen como 'undefined'.
	 *
	 * @returns
	 *	Una estructura comercial vacía.
	 */
	public static empty(): CommercialStructure {
		const commercialStructure: CommercialStructure = new CommercialStructure();
		commercialStructure.id = undefined;
		commercialStructure.name = "";
		commercialStructure.parent = undefined;
		commercialStructure.subStructure = [];
		return commercialStructure;
	}

	/**
	 * @description
	 *  Método de reificación de un modelo compatible anémico. Esto permite
	 *  ejecutar lógica de negocios sobre el modelo generado, siendo que el
	 *  valor de sus propiedades se mantiene.
	 *
	 * @param anemicCommercialStructure
	 *  El modelo anémico a reificar.
	 *
	 * @returns
	 *  Devuelve un modelo de esta clase, cuyas propiedades fueron copiadas en
	 *  profundidad desde el modelo anémico.
	 */
	public static from(anemicCommercialStructure: CommercialStructure): CommercialStructure {
		return CommercialStructure.fromWithParent(anemicCommercialStructure, undefined);
	}

	public ascendingPrefix(): number[] {
		const prefix: number[] = [undefined, undefined, undefined, undefined, undefined];
		const partialPrefix = CommercialStructure.partialAscendingPrefix(this);
		prefix.unshift(...partialPrefix);
		return prefix.slice(0, 5);
	}

	/**
	 * @description
	 *	Realiza una búsqueda ascendente desde el máximo nivel de profundidad
	 *	especificado en dirección hacia la raíz. La búsqueda se detiene cuando
	 *	se encuentra una estructura que coincida con el ID más profundo.
	 */
	public ascendingQueryById(
			countryId: number = undefined, commerceId: number = undefined,
			divisionId: number = undefined, zoneId: number = undefined,
			areaId: number = undefined): Optional<CommercialStructure> {
		return CommercialStructure
			.deepestLevel(countryId, commerceId, divisionId, zoneId, areaId)
			.map(tuple => CommercialStructure
				.level(this.subStructure, tuple.value1)
				.filter(structure => structure.id === tuple.value2))
			.flatMap(structures => {
				if (structures.length === 1) return Optional.of(structures[0]);
				else return Optional.empty();
			});
	}

	private static partialAscendingPrefix(structure: CommercialStructure): number[] {
		if (Optional.of(structure).isEmpty() || structure.isRootStructure()) {
			return [];
		}
		else {
			return CommercialStructure
				.partialAscendingPrefix(structure.parent)
				.concat([structure.id]);
		}
	}

	private static deepestLevel(
			countryId: number = undefined, commerceId: number = undefined,
			divisionId: number = undefined, zoneId: number = undefined,
			areaId: number = undefined): Optional<Tuple<Level, number>> {
		if (Optional.of(areaId).isPresent()) return Optional.of(Tuple.of(Level.Area, areaId));
		else if (Optional.of(zoneId).isPresent()) return Optional.of(Tuple.of(Level.Zone, zoneId));
		else if (Optional.of(divisionId).isPresent()) return Optional.of(Tuple.of(Level.Division, divisionId));
		else if (Optional.of(commerceId).isPresent()) return Optional.of(Tuple.of(Level.Commerce, commerceId));
		else if (Optional.of(countryId).isPresent()) return Optional.of(Tuple.of(Level.Country, countryId));
		else return Optional.empty();
	}

	public availableCountries(): number {
		if (this.isRootStructure()) {
			return CommercialStructure.levelSize(this.subStructure, Level.Country);
		}
		else return 0;
	}

	public availableCommerces(): number {
		if (this.isRootStructure()) {
			return CommercialStructure.levelSize(this.subStructure, Level.Commerce);
		}
		else return 0;
	}

	public availableDivisions(): number {
		if (this.isRootStructure()) {
			return CommercialStructure.levelSize(this.subStructure, Level.Division);
		}
		else return 0;
	}

	public availableZones(): number {
		if (this.isRootStructure()) {
			return CommercialStructure.levelSize(this.subStructure, Level.Zone);
		}
		else return 0;
	}

	public availableAreas(): number {
		if (this.isRootStructure()) {
			return CommercialStructure.levelSize(this.subStructure, Level.Area);
		}
		else return 0;
	}

	public isRootStructure(): boolean {
		return this.id === CommercialStructure.ROOT_ID
			&& this.name === CommercialStructure.ROOT_NAME && this.isInvoiced === CommercialStructure.ROOT_ISINVOICED;
	}

	/**
	 * @description
	 *	Computa el prefijo de esta estructura comercial, compuesto por los IDs
	 *	de las estructuras que ya están automáticamente definidas, debido a
	 *	que son únicas.
	 *
	 * @returns
	 *	Un vector donde cada componente posee el ID de la estructura asociada
	 *	al nivel correspondiente, pero 'undefined' a partir del nivel donde la
	 *	estructura no es única.
	 */
	public prefix(): number[] {
		const prefix: number[] = [];
		if (this.isRootStructure()) {
			if (this.availableCountries() === 1) {
				prefix.push(this.queryByIndex(0).value.id);
				if (this.availableCommerces() === 1) {
					prefix.push(this.queryByIndex(0, 0).value.id);
					if (this.availableDivisions() === 1) {
						prefix.push(this.queryByIndex(0, 0, 0).value.id);
						if (this.availableZones() === 1) {
							prefix.push(this.queryByIndex(0, 0, 0, 0).value.id);
							if (this.availableAreas() === 1) {
								prefix.push(this.queryByIndex(0, 0, 0, 0, 0).value.id);
							}
						}
					}
				}
			}
		}
		return prefix;
	}

	/**
	 * @description
	 *	Navega la estructura comercial hasta hallar el máximo nivel de
	 *	profundidad especificado. La búsqueda se realiza por ID.
	 *
	 * @returns
	 *	La estructura comercial buscada, o un opcional vacío si la misma no
	 *	existía en la estructura con las coordenadas especificadas.
	 */
	public queryById(
			countryId: number = undefined, commerceId: number = undefined,
			divisionId: number = undefined, zoneId: number = undefined,
			areaId: number = undefined): Optional<CommercialStructure> {
		return this.relativeQueryById(countryId)
			.flatMap(country => commerceId === undefined ? Optional.of(country) : country.relativeQueryById(commerceId))
			.flatMap(commerce => divisionId === undefined ? Optional.of(commerce) : commerce.relativeQueryById(divisionId))
			.flatMap(division => zoneId === undefined ? Optional.of(division) : division.relativeQueryById(zoneId))
			.flatMap(zone => areaId === undefined ? Optional.of(zone) : zone.relativeQueryById(areaId));
	}

	/**
	 * @description
	 *	Análogo al método "queryById(...)", pero realizando la búsqueda por
	 *	índice, es decir, por la posición absoluta dentro de cada estructura.
	 */
	public queryByIndex(
			countryIndex: number = undefined, commerceIndex: number = undefined,
			divisionIndex: number = undefined, zoneIndex: number = undefined,
			areaIndex: number = undefined): Optional<CommercialStructure> {
		return Optional.of(this.subStructure[countryIndex])
			.map(country => commerceIndex === undefined ? country : country.subStructure[commerceIndex])
			.map(commerce => divisionIndex === undefined ? commerce : commerce.subStructure[divisionIndex])
			.map(division => zoneIndex === undefined ? division : division.subStructure[zoneIndex])
			.map(zone => areaIndex === undefined ? zone : zone.subStructure[areaIndex]);
	}

	/**
	 * @description
	 *	Realiza una búsqueda sobre esta estructura comercial, sin descender a
	 *	niveles más profundos.
	 *
	 * @param id
	 *	El ID de la estructura a buscar.
	 *
	 * @returns
	 *	La estructura comercial con el ID especificado. E.g.: si el ID se
	 *	corresponde con una división, se emitirá una estructura que contiene
	 *	todas las zonas dentro de ella.
	 */
	public relativeQueryById(id: number): Optional<CommercialStructure> {
		return CommercialStructure.find(this.subStructure, id);
	}
    public relativeQueryByIdAndInvoiced(id: number): Optional<CommercialStructure> {
		return CommercialStructure.findInvoiced(this.subStructure, id);
	}

    public relativeQueryForDivision(id: number): Optional<CommercialStructure> {
        return CommercialStructure.find(this.subStructure, id);
    }

	private static level(subStructure: CommercialStructure[], depth: Level): CommercialStructure[] {
		if (depth <= Level.Country) {
			return subStructure;
		}
		else {
			return subStructure
				.map(structure => CommercialStructure.level(structure.subStructure, depth - 1))
				.reduce((accumulator, structures) => accumulator.concat(structures), []);
		};
	}

	private static levelSize(subStructure: CommercialStructure[], depth: Level): number {
		if (depth <= Level.Country) {
			return subStructure.length;
		}
		else {
			return subStructure
				.map(structure => CommercialStructure.levelSize(structure.subStructure, depth - 1))
				.reduce((accumulator, length) => accumulator + length, 0);
		};
	}

	private static find(subStructure: CommercialStructure[], id: number): Optional<CommercialStructure> {
		return Algorithm.binarySearch(subStructure, id, (commercialStructure, id) => commercialStructure.id - id);
	}

	private static fromWithParent(anemicCommercialStructure: CommercialStructure, parentStructure: CommercialStructure): CommercialStructure {
		if (!anemicCommercialStructure) return undefined;
		const commercialStructure: CommercialStructure = CommercialStructure.empty();
		commercialStructure.id = anemicCommercialStructure.id;
		commercialStructure.name = anemicCommercialStructure.name;
		commercialStructure.parent = parentStructure;
        commercialStructure.isInvoiced = anemicCommercialStructure.isInvoiced;
		commercialStructure.subStructure = TypeSystem.orElse(anemicCommercialStructure.subStructure, [])
			.map(_commercialStructure => CommercialStructure.fromWithParent(_commercialStructure, commercialStructure));
		return commercialStructure;
	}

    private static findInvoiced(subStructure: CommercialStructure[], id: number): Optional<CommercialStructure> {
        let sub = Algorithm.binarySearch(subStructure, id, (commercialStructure, id) => commercialStructure.id - id);
        if (sub.isPresent()) {
            sub.filter(structure => structure.isInvoiced == 1);
            return sub;
        }
        return Optional.empty();
    }
}
